import { CONVERSATIONAL_CHAT_DEADMANSWITCH } from '@klant/conversational-connector/klant-conversational-connector.constants';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';
import { AttachChannelModel } from '@klant/conversational-connector/models/AttachChannel.model';
import { BasePathService } from '@klant/conversational-connector/services/base-path/base-path.service';
import { ConversationService } from '@klant/conversational-connector/services/conversation/conversation.service';
import { KlantConversationalConnectorService } from '@klant/conversational-connector/klant-conversational-connector.service';
import { MessageHandlerService } from '@klant/conversational-connector/services/message-handle-service/message-handler.service';
import { UnreadMessageService } from '@klant/conversational-connector/services/unread-message-service/unread-message.service';
import { WebsocketService } from '@klant/conversational-connector/services/wsservice/websocket.service';
import { ChatSession, WebsocketResponse } from '@klant/conversational-connector/klant-conversational-connector.model';
import { FaqMessageService } from '@klant/conversational-connector/services/faq-message-service/faq-message.service';
import { InactivityService } from '@klant/conversational-connector/services/inactivity-service/inactivity.service';

import { concatMap, EMPTY } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';

import { ChannelName, MijnKanaalconfiguratieService } from '@ap/mijn-kanaalconfiguratie';
import { ApContentService } from '@ap/content';
import { ApError } from '@ap/error';

import { ChatSessionStore } from '../../../../signal-stores/chat-sesion-store/chat-session.store';
import { ConversationalChatStore } from '../../../../signal-stores/conversational-chat-store/conversational-chat.store';
import { SessionStorageService } from '../../../../session-storage/session-storage.service';

@Injectable({ providedIn: 'root' })
export class ConversationLifecycleService {
    private readonly basePathService = inject(BasePathService);
    private readonly chatSessionStore = inject(ChatSessionStore);
    private readonly chatStore = inject(ConversationalChatStore);
    private readonly contentService = inject(ApContentService);
    private readonly conversationService = inject(ConversationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly faqMessageService = inject(FaqMessageService);
    private readonly inactivityService = inject(InactivityService);
    private readonly klantConversationalConnectorService = inject(KlantConversationalConnectorService);
    private readonly messageHandlerService = inject(MessageHandlerService);
    private readonly mijnKanaalconfiguratieService = inject(MijnKanaalconfiguratieService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly unreadMessageService = inject(UnreadMessageService);
    private readonly webSocketService = inject(WebsocketService);

    public constructor() {
        this.listenToSocketErrors();
        this.listenToSocketResponses();
        this.createConversationWhenChatSessionIdIsSet();
    }

    public createConversation(): void {
        if (!this.basePathService.getIsLoadedCorrectly()) {
            this.setTakeoverErrorUnlessRetrying();
            return;
        }

        this.mijnKanaalconfiguratieService
            .getBooleanConfiguration(ChannelName.MIJN_KLANT, CONVERSATIONAL_CHAT_DEADMANSWITCH, false)
            .pipe(
                concatMap((deadManSwitch) => {
                    if (deadManSwitch) {
                        this.setTakeoverError(false);
                        return EMPTY;
                    }
                    return this.klantConversationalConnectorService.initializeSeamly(this.chatSessionStore.chatSession());
                })
            )
            .subscribe({
                next: (conversationResponse: ConversationModel) => {
                    this.chatStore.setConversation(conversationResponse);
                    this.initializeSocket(conversationResponse);
                },
                error: () => this.setTakeoverErrorUnlessRetrying()
            });
    }

    private setTakeoverErrorUnlessRetrying(): void {
        if (this.chatStore.connectionError() !== 'retry connection error') {
            this.setTakeoverError();
        }
    }

    private listenToSocketErrors(): void {
        this.webSocketService.connectionErrorSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) => {
                this.chatStore.setConnectionError(data);
            }
        });
    }

    private listenToSocketResponses(): void {
        this.webSocketService.connectionSubjectResponse.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) => {
                this.handleSocketResponse(data);
            }
        });
    }

    private createConversationWhenChatSessionIdIsSet(): void {
        toObservable(this.chatSessionStore.chatSession)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((chatSession: ChatSession) => {
                if (chatSession) {
                    this.createConversation();
                }
            });
    }

    private handleSocketResponse(response: WebsocketResponse): void {
        switch (response.type) {
            case TypeModel.ATTACH_CHANNEL:
                this.initializeConversation(response);
                break;
            case TypeModel.START_CONVERSATION:
                this.onStartConversation();
                break;
            case TypeModel.PLAIN_TEXT_MESSAGE:
            case TypeModel.CHOICE_PROMPT_MESSAGE:
            case TypeModel.CTA_MESSAGE:
                this.messageHandlerService.handleIncomingMessage(response);
                break;
            case TypeModel.SERVICE_ATTACH:
                this.inactivityService.handleServiceAttach(response);
                break;
            case TypeModel.PARTICIPANT_MESSAGE:
                this.messageHandlerService.handleParticipantMessage(response);
                break;
        }
    }

    private initializeSocket(conversationResponse: ConversationModel): void {
        let conversationSocketUrl: string;

        if (this.sessionStorageService.getSeamlyEnvironmentConfig().seamlyEnvironment === 'DEV') {
            conversationSocketUrl = `ws://${conversationResponse.webSocketLink}`;
        } else {
            conversationSocketUrl = `wss://${this.sessionStorageService.getSeamlyEnvironmentConfig().seamlyApiDomain}${conversationResponse.webSocketLink}`;
        }

        if (this.webSocketService.subject && !this.webSocketService.subject.closed) {
            this.resetConversation();
            return;
        }

        this.webSocketService.subject = this.webSocketService.connect(conversationSocketUrl, () => {
            this.webSocketService.attachSocketChannel(conversationResponse);
            this.setSeamlyVariables();
        });

        this.webSocketService.subscribeSocket();
    }

    private initializeConversation(attachChannelModel: AttachChannelModel): void {
        if (attachChannelModel.success) {
            this.webSocketService.startConversation();

            if (this.chatStore.conversation().chatMessages.length) {
                !!this.chatStore.connectionError() ? this.resetConversation() : this.setChatMessages();
            }
        } else {
            this.setTakeoverError();
        }
    }

    private resetConversation(): void {
        this.webSocketService.messageLoading.set(false);
        this.conversationService.getConversation().subscribe({
            next: (conversationResponse) => {
                this.chatStore.setConversation(conversationResponse);
                this.chatStore.setConnectionError(null);
                this.setChatMessages();
                this.messageHandlerService.resendPendingMessages();
            },
            error: () => this.setTakeoverError()
        });
    }

    private setChatMessages(): void {
        const conversation = this.chatStore.conversation();
        let messages = [...conversation.chatMessages];

        this.chatStore.setChatMessages(messages);
        this.chatStore.setIdleTimerActive(conversation.resumeConversationPrompt);
        this.unreadMessageService.markMessagesAsRead(this.chatStore.chatMessages());
    }

    private onStartConversation(): void {
        this.klantConversationalConnectorService.chatFullyLoaded = true;
        this.faqMessageService.sendFaqMessage();
    }

    private setTakeoverError(showReloadOption = true): void {
        this.chatStore.setTakeoverError(
            new ApError(
                this.contentService.get('klant.conversational.connector.error.message'),
                undefined,
                null,
                showReloadOption,
                this.contentService.get('klant.conversational.connector.error.message.reload')
            )
        );
    }

    private setSeamlyVariables(): void {
        this.webSocketService.setSeamlyContextVariables(
            this.chatStore.platform(),
            this.sessionStorageService.getSeamlyEnvironmentConfig().seamlyEnvironment
        );
    }
}
